import React, { useMemo, useState, useEffect } from 'react';
import { cn } from '@/lib/utils';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Separator } from '@/components/ui/separator';
import { Progress } from '@/components/ui/progress';
import {
  Shield, Download, FileText, CheckCircle2, XCircle, AlertTriangle,
  ArrowLeft, TrendingUp, TrendingDown, Layers, Target, ClipboardList,
  Server, Lock, Activity, Building2, FileWarning, BookOpen, Rocket, Clock,
  Wrench, Globe, HardDrive, FolderCheck, BadgeCheck
} from 'lucide-react';

// Import lib functions
import { generateReport, buildExecutiveSnapshotPayload } from '@/lib/report-generator';
import { exportPDF, exportExecutiveSnapshotPDF } from '@/lib/pdf-generator';
import { downloadAsJSON } from '@/lib/storage';
import { getReportFilename } from '@/lib/report-generator';
import { SECTION_CONFIG, LAYER_LABELS, COMPONENT_LABELS } from '@/config/domain-weights';
import { toast } from 'sonner';
import HybridReportSections from '@/components/HybridReportSections';
import PdfPresetDialog from '@/components/PdfPresetDialog';
import StackHealthTrend from '@/components/StackHealthTrend';
import { PDF_PRESETS } from '@/lib/pdf-presets';
import { useAuth } from '@/contexts/AuthContext';

// Helper functions
const getScoreColor = (score) => {
  if (score >= 85) return 'text-green-600';
  if (score >= 65) return 'text-blue-600';
  if (score >= 40) return 'text-amber-600';
  return 'text-red-600';
};

const getScoreBg = (score) => {
  if (score >= 85) return 'bg-green-100';
  if (score >= 65) return 'bg-blue-100';
  if (score >= 40) return 'bg-amber-100';
  return 'bg-red-100';
};

const getMaturityLabel = (score) => {
  if (score >= 85) return 'Strong';
  if (score >= 65) return 'Moderate';
  if (score >= 40) return 'Developing';
  return 'Poor';
};

const prettyStatus = (status) => {
  const labels = {
    not_ready: 'Not Ready',
    partially_ready: 'Partially Ready',
    substantially_ready: 'Substantially Ready',
    ready_for_validation: 'Ready for Validation'
  };
  return labels[status] || status;
};

const getPriorityColor = (priority) => {
  switch (priority) {
    case 'critical': return 'bg-red-100 text-red-700 border-red-200';
    case 'high': return 'bg-amber-100 text-amber-700 border-amber-200';
    case 'medium': return 'bg-blue-100 text-blue-700 border-blue-200';
    default: return 'bg-slate-100 text-slate-700 border-slate-200';
  }
};

export default function FinalReportPanel({
  engagement,
  answers,
  onBack
}) {
  const { authFetch } = useAuth();
  const [consultancyBranding, setConsultancyBranding] = useState(null);

  // Fetch consultancy-wide branding (global default; per-engagement client logo overrides on cover)
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await authFetch('/api/branding');
        if (res.ok && !cancelled) {
          const data = await res.json();
          setConsultancyBranding(data || null);
        }
      } catch (_e) {
        // silent: branding is optional
      }
    })();
    return () => { cancelled = true; };
  }, [authFetch]);

  // Build branding context for report + PDF (per-engagement client logo always wins on cover)
  const branding = useMemo(() => ({
    consultancy_name: consultancyBranding?.consultancy_name || 'DefencIQ',
    consultancy_logo_base64: consultancyBranding?.consultancy_logo_base64 || null,
    primary_color_hex: consultancyBranding?.primary_color_hex || null,
    client_logo_base64: engagement?.client_logo_base64 || null,
  }), [consultancyBranding, engagement?.client_logo_base64]);

  // Generate the full report (now includes branding metadata)
  const report = useMemo(
    () => generateReport(engagement, answers, { branding }),
    [engagement, answers, branding]
  );
  
  const {
    derived_outputs: derivedOutputs,
    _report_metadata: metadata
  } = report;

  // PDF export - preset picker gate
  const [pdfDialogOpen, setPdfDialogOpen] = useState(false);
  const handleExportPDF = () => setPdfDialogOpen(true);

  const handleExportPDFWithPreset = (presetId) => {
    try {
      const filename = exportPDF(report, engagement, { preset: presetId });
      const label = PDF_PRESETS[presetId]?.label || presetId;
      toast.success(`PDF exported - ${label}`, { description: filename });
    } catch (error) {
      console.error('PDF export error:', error);
      toast.error('Failed to export PDF');
    }
  };

  // Handle Executive Snapshot PDF export
  const handleExportExecutiveSnapshot = () => {
    try {
      const payload = buildExecutiveSnapshotPayload(engagement, report);
      const filename = exportExecutiveSnapshotPDF(payload);
      toast.success('Executive Snapshot exported', { description: filename });
    } catch (error) {
      console.error('Executive Snapshot export error:', error);
      toast.error('Failed to export Executive Snapshot');
    }
  };

  // Handle JSON export
  const handleExportJSON = () => {
    const filename = getReportFilename(engagement);
    downloadAsJSON(report, filename);
    toast.success('JSON exported', { description: filename });
  };

  const overallScore = derivedOutputs?.overall_ml2_readiness_score || 0;
  const overallStatus = derivedOutputs?.overall_status || 'not_ready';
  const gatingControls = metadata?.gating_control_details || [];
  const domainFindings = metadata?.domain_findings || [];
  const priorityGaps = derivedOutputs?.priority_gaps || [];
  const stackByLayer = metadata?.stack_by_layer || {};
  const roadmap = metadata?.remediation_roadmap || {};
  const dispReadiness = metadata?.disp_readiness || {};
  const blueprint = metadata?.blueprint || null;

  // Get top 3 weakest domains
  const weakestDomains = [...domainFindings]
    .sort((a, b) => a.score - b.score)
    .slice(0, 3);

  // Gating stats
  const gatingPassed = gatingControls.filter(c => c.passed).length;
  const gatingFailed = gatingControls.filter(c => c.score === 0).length;
  const gatingPartial = gatingControls.filter(c => c.score === 1).length;

  return (
    <div className="min-h-screen bg-slate-50 p-4 md:p-8" data-testid="final-report-panel">
      <PdfPresetDialog
        open={pdfDialogOpen}
        onOpenChange={setPdfDialogOpen}
        onConfirm={handleExportPDFWithPreset}
      />
      <div className="mx-auto max-w-5xl space-y-6">
        
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <Button 
              variant="ghost" 
              onClick={onBack}
              className="mb-2 -ml-2 text-slate-600 hover:text-slate-900"
            >
              <ArrowLeft className="mr-2 h-4 w-4" />
              Back to Assessment
            </Button>
            {/* Branded report title strip with consultancy + client logo */}
            <div className="flex items-center gap-4">
              {(branding.consultancy_logo_base64 || branding.client_logo_base64) && (
                <div className="flex items-center gap-3" data-testid="branded-header-strip">
                  {branding.client_logo_base64 && (
                    <div className="h-12 px-2 rounded-md bg-white flex items-center border border-slate-200" data-testid="branded-client-logo">
                      <img
                        src={branding.client_logo_base64}
                        alt={`${engagement.client_name || 'Client'} logo`}
                        className="max-h-8 max-w-[120px] object-contain"
                      />
                    </div>
                  )}
                  {branding.consultancy_logo_base64 && (
                    <div className="text-xs text-slate-500">
                      Prepared by{' '}
                      <span className="font-semibold text-slate-700">{branding.consultancy_name}</span>
                    </div>
                  )}
                </div>
              )}
            </div>
            <h1 className="text-3xl font-bold text-slate-900 mt-3">Discovery Assessment Report</h1>
            <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1.5">
              <p className="text-slate-600">
                {engagement.client_name} • {engagement.assessment_date}
              </p>
              {engagement.assessor_name && String(engagement.assessor_name).trim() !== '' && (
                <span
                  className="inline-flex items-center gap-1.5 rounded-full border border-emerald-200 bg-emerald-50 px-2.5 py-0.5 text-xs font-medium text-emerald-700"
                  title={`Assessor declared: ${engagement.assessor_name}`}
                  data-testid="assessor-declared-chip"
                >
                  <BadgeCheck className="h-3.5 w-3.5" strokeWidth={2.25} />
                  <span>Assessor Declared</span>
                  <span className="hidden sm:inline text-emerald-600/80">·</span>
                  <span className="hidden sm:inline text-emerald-800">{engagement.assessor_name}</span>
                </span>
              )}
            </div>
          </div>
          <div className="flex gap-3">
            <Button 
              variant="outline" 
              onClick={handleExportJSON}
              className="rounded-xl"
            >
              <FileText className="mr-2 h-4 w-4" />
              Export JSON
            </Button>
            <Button 
              variant="outline"
              onClick={handleExportExecutiveSnapshot}
              className="rounded-xl"
              data-testid="export-executive-snapshot-btn"
            >
              <Rocket className="mr-2 h-4 w-4" />
              Executive Snapshot
            </Button>
            <Button 
              onClick={handleExportPDF}
              className="rounded-xl bg-slate-900 hover:bg-slate-800"
            >
              <Download className="mr-2 h-4 w-4" />
              Export PDF
            </Button>
          </div>
        </div>

        {/* Executive Summary Cards */}
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-5" data-testid="executive-summary-cards">
          {/* Overall Score */}
          <Card className="rounded-3xl border-0 shadow-sm">
            <CardContent className="pt-6">
              <div className="text-sm text-slate-500 mb-2">Overall Score</div>
              <div className={cn('text-4xl font-bold', getScoreColor(overallScore))}>
                {overallScore}%
              </div>
              <Badge className={cn('mt-2', getScoreBg(overallScore), getScoreColor(overallScore))}>
                {getMaturityLabel(overallScore)}
              </Badge>
            </CardContent>
          </Card>

          {/* Readiness Status */}
          <Card className="rounded-3xl border-0 shadow-sm">
            <CardContent className="pt-6">
              <div className="text-sm text-slate-500 mb-2">Readiness Status</div>
              <div className="text-xl font-bold text-slate-900">
                {prettyStatus(overallStatus)}
              </div>
              <p className="text-sm text-slate-500 mt-2">
                {overallStatus === 'ready_for_validation' ? 'Ready for audit'
                  : overallStatus === 'substantially_ready' ? 'Minor gaps remain'
                  : overallStatus === 'partially_ready' ? 'Significant gaps'
                  : 'Critical gaps identified'}
              </p>
            </CardContent>
          </Card>

          {/* Evidence Readiness — mirrors the snapshot signal */}
          {metadata?.hybrid_report?.evidence_readiness && (
            <Card className="rounded-3xl border-0 shadow-sm" data-testid="card-evidence-readiness">
              <CardContent className="pt-6">
                <div className="text-sm text-slate-500 mb-2">Evidence Readiness</div>
                <div className={cn(
                  'text-xl font-bold',
                  metadata.hybrid_report.evidence_readiness.evidence_maturity_score === 'High' ? 'text-emerald-700' :
                  metadata.hybrid_report.evidence_readiness.evidence_maturity_score === 'Medium' ? 'text-amber-700' :
                  'text-red-700'
                )}>
                  {metadata.hybrid_report.evidence_readiness.evidence_maturity_score}
                </div>
                <p className="text-xs text-slate-500 mt-2 leading-snug line-clamp-3">
                  {metadata.hybrid_report.evidence_readiness.evidence_maturity_score === 'High'
                    ? 'Audit-ready evidence is centrally maintained and traceable to controls.'
                    : metadata.hybrid_report.evidence_readiness.evidence_maturity_score === 'Medium'
                    ? 'Evidence is fragmented and not consistently linked to controls.'
                    : 'Audit-ready evidence is not centralised or aligned to controls.'}
                </p>
              </CardContent>
            </Card>
          )}

          {/* Classification - Environment Type */}
          <Card className="rounded-3xl border-0 shadow-sm">
            <CardContent className="pt-6">
              <div className="text-sm text-slate-500 mb-2">Environment Type</div>
              <div className={cn(
                'text-lg font-bold',
                derivedOutputs?.environment_type === 'greenfield' ? 'text-red-600' :
                derivedOutputs?.environment_type === 'fragmented_adhoc' ? 'text-amber-600' :
                derivedOutputs?.environment_type === 'structured_non_compliant' ? 'text-blue-600' : 'text-green-600'
              )}>
                {derivedOutputs?.environment_type_info?.label || 'Unknown'}
              </div>
              <p className="text-sm text-slate-500 mt-2">
                {derivedOutputs?.environment_type_info?.description || ''}
              </p>
            </CardContent>
          </Card>

          {/* Service Tier */}
          <Card className="rounded-3xl border-0 shadow-sm">
            <CardContent className="pt-6">
              <div className="text-sm text-slate-500 mb-2">Recommended Service</div>
              <div className="text-lg font-bold text-slate-900">
                {derivedOutputs?.service_tier_info?.label || 'Unknown'}
              </div>
              <p className="text-sm text-slate-500 mt-2">
                {derivedOutputs?.service_tier_info?.description || ''}
              </p>
            </CardContent>
          </Card>
        </div>

        {/* ==================== STACK HEALTH TREND ==================== */}
        {/* Renders only when the client has >= 2 assessments (self-hidden). */}
        <StackHealthTrend
          clientName={engagement.client_name}
          currentAssessmentDate={engagement.assessment_date}
        />

        {/* ==================== EXECUTIVE BRIEF ==================== */}
        {metadata?.executive_brief && (
          <Card className="rounded-3xl border-0 shadow-sm bg-white" data-testid="executive-brief">
            <CardHeader className="pb-2">
              <div className="flex items-center gap-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-2xl bg-slate-900">
                  <BookOpen className="h-5 w-5 text-white" />
                </div>
                <div>
                  <CardTitle className="text-xl">Executive Brief</CardTitle>
                  <CardDescription>A concise overview for decision-makers</CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* EB.1 DISP Readiness removed - covered by Hybrid HY.1 Executive Snapshot */}
              {/* EB.2 Business Risk removed - covered by Hybrid HY.2 Commercial Impact */}

              {/* 3. Key Gaps */}
              {metadata.executive_brief.key_gaps.length > 0 && (
                <div>
                  <h3 className="text-sm font-semibold text-slate-900 mb-3">What's Holding You Back</h3>
                  <div className="space-y-2">
                    {metadata.executive_brief.key_gaps.map((gap, i) => (
                      <div key={i} className="flex items-start gap-2.5 p-2.5 rounded-xl bg-red-50 border border-red-100">
                        <XCircle className="h-4 w-4 text-red-500 mt-0.5 shrink-0" />
                        <span className="text-sm text-slate-800">{gap}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* EB.4 What Needs to Be Done removed - covered by Hybrid HY.9 Path to DISP Readiness */}
              {/* EB.5 Maturity Snapshot removed - covered by TS.1 Scoring Component Breakdown */}
            </CardContent>
          </Card>
        )}

        {/* ==================== HYBRID DISP READINESS REPORT (9 SECTIONS) ==================== */}
        {metadata?.hybrid_report && (
          <HybridReportSections hybridReport={metadata.hybrid_report} />
        )}

        {/* ==================== EVIDENCE READINESS ASSESSMENT ==================== */}
        {metadata?.evidence_readiness_assessment && (() => {
          const er = metadata.evidence_readiness_assessment;
          const bandBg = !er.assessed ? 'bg-slate-100 text-slate-600'
            : er.band?.color === 'emerald' ? 'bg-emerald-100 text-emerald-800'
            : er.band?.color === 'sky' ? 'bg-sky-100 text-sky-800'
            : er.band?.color === 'amber' ? 'bg-amber-100 text-amber-800'
            : 'bg-rose-100 text-rose-800';
          return (
            <Card className="rounded-3xl border-0 shadow-sm" data-testid="evidence-readiness-report-section">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <FileText className="h-5 w-5" />
                  Evidence Readiness
                </CardTitle>
                <CardDescription>
                  {er.assessed
                    ? 'Reported presence of major evidence categories (Discovery-level check only). Detailed validation occurs during Accelerator.'
                    : 'Not assessed in this engagement.'}
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {!er.assessed ? (
                  <div className="rounded-xl bg-slate-50 border border-slate-200 p-4 text-sm text-slate-600">
                    Evidence Readiness: Not Assessed
                  </div>
                ) : (
                  <>
                    {/* Overall + counts */}
                    <div className="grid gap-4 md:grid-cols-4">
                      <div className="md:col-span-1 rounded-2xl border border-slate-200 bg-slate-50 p-4">
                        <p className="text-xs uppercase tracking-wide text-slate-500">Overall Score</p>
                        <p className="mt-1 text-3xl font-bold text-slate-900" data-testid="er-overall-score">{er.overall_percent}%</p>
                        <span className={cn('inline-flex mt-2 items-center px-2.5 py-0.5 rounded-full text-xs font-semibold', bandBg)}>
                          {er.band.label}
                        </span>
                      </div>
                      <div className="rounded-2xl bg-emerald-50 border border-emerald-100 p-4">
                        <p className="text-xs text-emerald-700 uppercase tracking-wide">Available</p>
                        <p className="text-2xl font-semibold text-emerald-700 mt-1">{er.counts.available}</p>
                      </div>
                      <div className="rounded-2xl bg-amber-50 border border-amber-100 p-4">
                        <p className="text-xs text-amber-700 uppercase tracking-wide">Partial</p>
                        <p className="text-2xl font-semibold text-amber-700 mt-1">{er.counts.partial}</p>
                      </div>
                      <div className="rounded-2xl bg-rose-50 border border-rose-100 p-4">
                        <p className="text-xs text-rose-700 uppercase tracking-wide">Missing</p>
                        <p className="text-2xl font-semibold text-rose-700 mt-1">{er.counts.missing}</p>
                      </div>
                    </div>

                    {/* Per-domain */}
                    <div>
                      <h4 className="text-sm font-semibold text-slate-900 mb-3">Evidence Readiness by DISP Domain</h4>
                      <div className="space-y-2">
                        {er.per_domain.map(d => (
                          <div key={d.domain} className="flex items-center justify-between rounded-xl border border-slate-200 bg-white p-3">
                            <span className="text-sm text-slate-700">{d.label}</span>
                            <div className="flex items-center gap-3">
                              {d.band && (
                                <span className={cn(
                                  'text-[11px] font-semibold uppercase tracking-wide',
                                  d.band.color === 'emerald' ? 'text-emerald-700'
                                    : d.band.color === 'sky' ? 'text-sky-700'
                                    : d.band.color === 'amber' ? 'text-amber-700'
                                    : 'text-rose-700'
                                )}>{d.band.label}</span>
                              )}
                              <span className="text-sm font-semibold text-slate-900 tabular-nums w-14 text-right">
                                {d.percent === null ? 'Not Assessed' : `${d.percent}%`}
                              </span>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Evidence Gaps */}
                    {er.gaps.by_domain.length > 0 && (
                      <div>
                        <h4 className="text-sm font-semibold text-slate-900 mb-3">Evidence Gaps</h4>
                        <div className="space-y-3">
                          {er.gaps.by_domain.map(d => (
                            <div key={d.domain} className="rounded-xl border border-slate-200 bg-white p-4">
                              <p className="text-sm font-semibold text-slate-800 mb-2">{d.label}</p>
                              {d.not_available.length > 0 && (
                                <div className="mb-2">
                                  <p className="text-xs uppercase tracking-wide text-rose-700 mb-1">Not Available</p>
                                  <ul className="space-y-0.5">
                                    {d.not_available.map(n => (
                                      <li key={n} className="text-xs text-slate-700 flex items-start gap-2">
                                        <span className="mt-1 w-1.5 h-1.5 rounded-full bg-rose-500 shrink-0" />
                                        {n}
                                      </li>
                                    ))}
                                  </ul>
                                </div>
                              )}
                              {d.partially_available.length > 0 && (
                                <div>
                                  <p className="text-xs uppercase tracking-wide text-amber-700 mb-1">Partially Available</p>
                                  <ul className="space-y-0.5">
                                    {d.partially_available.map(n => (
                                      <li key={n} className="text-xs text-slate-700 flex items-start gap-2">
                                        <span className="mt-1 w-1.5 h-1.5 rounded-full bg-amber-500 shrink-0" />
                                        {n}
                                      </li>
                                    ))}
                                  </ul>
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Narrative */}
                    <div className="rounded-xl border-l-4 border-slate-300 bg-slate-50 p-4">
                      <p className="text-sm text-slate-700 leading-relaxed">{er.narrative}</p>
                    </div>

                    {/* Accelerator Handoff */}
                    <div className="rounded-xl bg-indigo-50 border border-indigo-100 p-4">
                      <p className="text-sm font-semibold text-indigo-900 mb-1">Next Step — Evidence Validation</p>
                      <p className="text-xs text-indigo-800 leading-relaxed">
                        Evidence identified during Discovery should be collected, reviewed and mapped to applicable DISP requirements within DefencIQ Accelerator. Accelerator provides detailed artefact management, evidence validation and submission-readiness tracking.
                      </p>
                    </div>
                  </>
                )}
              </CardContent>
            </Card>
          );
        })()}

        {/* ==================== DISP GAP ANALYSIS ==================== */}
        {metadata?.disp_gap_analysis?.length > 0 && (
          <Card className="rounded-3xl border-0 shadow-sm" data-testid="disp-gap-analysis">
            <CardHeader className="pb-3">
              <div className="flex items-center gap-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-2xl bg-red-900">
                  <Shield className="h-5 w-5 text-white" />
                </div>
                <div>
                  <CardTitle className="text-xl">DISP Gap Analysis</CardTitle>
                  <CardDescription>Defence-aligned, audit-ready control assessment grouped by DISP domain</CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-8">
              {metadata.disp_gap_analysis.map((domain) => (
                <div key={domain.domain_id} data-testid={`disp-domain-${domain.domain_id}`}>
                  {/* Domain Header */}
                  <div className="flex items-center gap-2 mb-4 pb-2 border-b-2 border-slate-200">
                    <h3 className="text-base font-bold text-slate-900 uppercase tracking-wide">{domain.label}</h3>
                    <Badge variant="outline" className="text-xs text-slate-500 border-slate-300">
                      {domain.controls.length} {domain.controls.length === 1 ? 'control' : 'controls'}
                    </Badge>
                  </div>

                  {/* Controls */}
                  <div className="space-y-5">
                    {domain.controls.map((ctrl, ci) => (
                      <div
                        key={ctrl.group_id || ci}
                        className={cn(
                          'rounded-xl border p-4',
                          ctrl.compliance_status === 'non_compliant' ? 'border-red-200 bg-red-50/40' :
                          ctrl.compliance_status === 'partially_compliant' ? 'border-amber-200 bg-amber-50/40' :
                          'border-green-200 bg-green-50/40'
                        )}
                        data-testid={`gap-control-${ctrl.group_id || ci}`}
                      >
                        {/* Control Header */}
                        <div className="flex items-start justify-between gap-3 mb-2">
                          <div className="flex items-center gap-2 flex-wrap">
                            <h4 className="text-sm font-bold text-slate-900">{ctrl.short_name}</h4>
                            {ctrl.is_gating && (
                              <Badge className="bg-red-600 text-white text-[10px] px-1.5 py-0">GATING</Badge>
                            )}
                          </div>
                          <Badge className={cn(
                            'text-xs shrink-0',
                            ctrl.compliance_status === 'non_compliant' ? 'bg-red-100 text-red-700 border-red-200' :
                            ctrl.compliance_status === 'partially_compliant' ? 'bg-amber-100 text-amber-700 border-amber-200' :
                            'bg-green-100 text-green-700 border-green-200'
                          )}>
                            {ctrl.compliance_status === 'non_compliant' ? 'Non-Compliant' :
                             ctrl.compliance_status === 'partially_compliant' ? 'Partially Compliant' :
                             'Compliant'}
                          </Badge>
                        </div>

                        {/* Control count summary */}
                        {ctrl.control_count > 1 && (
                          <div className="flex items-center gap-2 mb-3 text-[11px] text-slate-500">
                            <span>{ctrl.control_count} controls assessed</span>
                            {ctrl.fail_count > 0 && <span className="text-red-600 font-medium">{ctrl.fail_count} failed</span>}
                            {ctrl.partial_count > 0 && <span className="text-amber-600 font-medium">{ctrl.partial_count} partial</span>}
                          </div>
                        )}

                        {/* Requirement */}
                        <div className="mb-3">
                          <p className="text-[11px] font-semibold text-slate-500 uppercase tracking-wide mb-1">Requirement</p>
                          <p className="text-xs text-slate-700 leading-relaxed">{ctrl.requirement}</p>
                        </div>

                        {/* Finding */}
                        {ctrl.compliance_status !== 'compliant' && (
                          <div className="mb-3">
                            <p className="text-[11px] font-semibold text-slate-500 uppercase tracking-wide mb-1">Finding</p>
                            <p className="text-xs text-slate-700 leading-relaxed">{ctrl.finding}</p>
                          </div>
                        )}

                        {/* Recommendation */}
                        {ctrl.compliance_status !== 'compliant' && (
                          <div className="mb-3">
                            <p className="text-[11px] font-semibold text-slate-500 uppercase tracking-wide mb-1">Recommendation</p>
                            <p className="text-xs text-slate-700 leading-relaxed">{ctrl.recommendation}</p>
                          </div>
                        )}

                        {/* Evidence Required */}
                        <div>
                          <p className="text-[11px] font-semibold text-slate-500 uppercase tracking-wide mb-1">Evidence Required</p>
                          <div className="flex flex-wrap gap-1.5">
                            {ctrl.evidence_required.map((ev, ei) => (
                              <span key={ei} className="text-[10px] text-slate-600 bg-white border border-slate-200 rounded-md px-2 py-0.5">
                                {ev}
                              </span>
                            ))}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </CardContent>
          </Card>
        )}

        {/* ==================== TECHNICAL SUMMARY DIVIDER ==================== */}
        <div className="flex items-center gap-4 py-2" data-testid="technical-summary-divider">
          <Separator className="flex-1" />
          <span className="text-sm font-semibold text-slate-400 uppercase tracking-wider whitespace-nowrap">Technical Summary</span>
          <Separator className="flex-1" />
        </div>

        {/* Enhanced Scoring Section */}
        {metadata?.enhanced_scores && (
          <Card className="rounded-3xl border-0 shadow-sm">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <TrendingUp className="h-5 w-5" />
                Weighted Scoring Breakdown
              </CardTitle>
              <CardDescription>
                Score components, multipliers applied, and detection alerts
              </CardDescription>
            </CardHeader>
            <CardContent>
              {/* Group Scores */}
              <div className="grid gap-4 md:grid-cols-3 mb-6">
                {/* CES */}
                <div className="rounded-2xl bg-slate-100 p-4">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-medium text-slate-600">CES (50%)</span>
                    <span className={cn(
                      'text-2xl font-bold',
                      getScoreColor(metadata.enhanced_scores.groupScores?.CES || 0)
                    )}>
                      {metadata.enhanced_scores.groupScores?.CES || 0}%
                    </span>
                  </div>
                  <p className="text-xs text-slate-500">Control Effectiveness Score</p>
                  <Progress value={metadata.enhanced_scores.groupScores?.CES || 0} className="h-2 mt-2" />
                </div>
                
                {/* PGS */}
                <div className="rounded-2xl bg-slate-100 p-4">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-medium text-slate-600">PGS (30%)</span>
                    <span className={cn(
                      'text-2xl font-bold',
                      getScoreColor(metadata.enhanced_scores.groupScores?.PGS || 0)
                    )}>
                      {metadata.enhanced_scores.groupScores?.PGS || 0}%
                    </span>
                  </div>
                  <p className="text-xs text-slate-500">Policy & Governance Score</p>
                  <Progress value={metadata.enhanced_scores.groupScores?.PGS || 0} className="h-2 mt-2" />
                </div>
                
                {/* CMS */}
                <div className="rounded-2xl bg-slate-100 p-4">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-medium text-slate-600">CMS (20%)</span>
                    <span className={cn(
                      'text-2xl font-bold',
                      getScoreColor(metadata.enhanced_scores.groupScores?.CMS || 0)
                    )}>
                      {metadata.enhanced_scores.groupScores?.CMS || 0}%
                    </span>
                  </div>
                  <p className="text-xs text-slate-500">Compliance Maturity Score</p>
                  <Progress value={metadata.enhanced_scores.groupScores?.CMS || 0} className="h-2 mt-2" />
                </div>
              </div>
              
              {/* Multipliers Applied */}
              {(metadata.enhanced_scores.multipliers?.policyEnforcement < 1 || 
                metadata.enhanced_scores.multipliers?.evidence < 1 ||
                metadata.enhanced_scores.multipliers?.confidence < 1) && (
                <div className="mb-6">
                  <h4 className="text-sm font-semibold text-slate-700 mb-3">Score Adjustments Applied</h4>
                  <div className="flex flex-wrap gap-2">
                    {metadata.enhanced_scores.multipliers?.policyEnforcement < 1 && (
                      <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-200">
                        Policy Enforcement ×{metadata.enhanced_scores.multipliers.policyEnforcement.toFixed(2)}
                      </Badge>
                    )}
                    {metadata.enhanced_scores.multipliers?.evidence < 1 && (
                      <Badge variant="outline" className="bg-orange-50 text-orange-700 border-orange-200">
                        Evidence Quality ×{metadata.enhanced_scores.multipliers.evidence.toFixed(2)}
                      </Badge>
                    )}
                    {metadata.enhanced_scores.multipliers?.confidence < 1 && (
                      <Badge variant="outline" className="bg-slate-100 text-slate-700 border-slate-200">
                        Confidence ×{metadata.enhanced_scores.multipliers.confidence.toFixed(2)} ({metadata.enhanced_scores.multipliers.unknownCount} unknowns)
                      </Badge>
                    )}
                  </div>
                </div>
              )}
              
              {/* Detection Flags */}
              {metadata.enhanced_scores.flags?.length > 0 && (
                <div>
                  <h4 className="text-sm font-semibold text-slate-700 mb-3">Detection Alerts</h4>
                  <div className="space-y-2">
                    {metadata.enhanced_scores.flags.map((flag) => (
                      <div 
                        key={flag.id}
                        className={cn(
                          "rounded-xl p-3 border-l-4",
                          flag.severity === 'critical' && 'bg-red-50 border-l-red-500',
                          flag.severity === 'high' && 'bg-orange-50 border-l-orange-500',
                          flag.severity === 'medium' && 'bg-yellow-50 border-l-yellow-500'
                        )}
                      >
                        <div className="flex items-center gap-2">
                          <AlertTriangle className={cn(
                            "h-4 w-4",
                            flag.severity === 'critical' && 'text-red-500',
                            flag.severity === 'high' && 'text-orange-500',
                            flag.severity === 'medium' && 'text-yellow-600'
                          )} />
                          <span className={cn(
                            "text-sm font-semibold",
                            flag.severity === 'critical' && 'text-red-700',
                            flag.severity === 'high' && 'text-orange-700',
                            flag.severity === 'medium' && 'text-yellow-700'
                          )}>
                            {flag.label}
                          </span>
                        </div>
                        <p className="text-xs text-slate-600 mt-1 ml-6">{flag.description}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              
              {/* Audit Failure Likelihood */}
              {metadata.enhanced_scores.auditFailureLikelihood && (
                <>
                  <Separator className="my-4" />
                  <div className={cn(
                    "rounded-xl p-4",
                    metadata.enhanced_scores.auditFailureLikelihood === 'CRITICAL' && 'bg-red-50 border-2 border-red-400',
                    metadata.enhanced_scores.auditFailureLikelihood === 'HIGH' && 'bg-orange-50 border-2 border-orange-400',
                    metadata.enhanced_scores.auditFailureLikelihood === 'MODERATE' && 'bg-yellow-50 border border-yellow-300',
                    metadata.enhanced_scores.auditFailureLikelihood === 'LOW' && 'bg-green-50 border border-green-300'
                  )}>
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <AlertTriangle className={cn(
                          "h-5 w-5",
                          metadata.enhanced_scores.auditFailureLikelihood === 'CRITICAL' && 'text-red-600',
                          metadata.enhanced_scores.auditFailureLikelihood === 'HIGH' && 'text-orange-600',
                          metadata.enhanced_scores.auditFailureLikelihood === 'MODERATE' && 'text-yellow-600',
                          metadata.enhanced_scores.auditFailureLikelihood === 'LOW' && 'text-green-600'
                        )} />
                        <span className="text-sm font-semibold text-slate-700">Audit Failure Risk</span>
                      </div>
                      <Badge className={cn(
                        "font-bold",
                        metadata.enhanced_scores.auditFailureLikelihood === 'CRITICAL' && 'bg-red-600 text-white',
                        metadata.enhanced_scores.auditFailureLikelihood === 'HIGH' && 'bg-orange-500 text-white',
                        metadata.enhanced_scores.auditFailureLikelihood === 'MODERATE' && 'bg-yellow-500 text-white',
                        metadata.enhanced_scores.auditFailureLikelihood === 'LOW' && 'bg-green-500 text-white'
                      )}>
                        {metadata.enhanced_scores.auditFailureLikelihood}
                      </Badge>
                    </div>
                    <p className={cn(
                      "text-sm mb-3",
                      metadata.enhanced_scores.auditFailureLikelihood === 'CRITICAL' && 'text-red-800',
                      metadata.enhanced_scores.auditFailureLikelihood === 'HIGH' && 'text-orange-800',
                      metadata.enhanced_scores.auditFailureLikelihood === 'MODERATE' && 'text-yellow-800',
                      metadata.enhanced_scores.auditFailureLikelihood === 'LOW' && 'text-green-800'
                    )}>
                      {metadata.enhanced_scores.auditFailureMessage}
                    </p>
                    {metadata.enhanced_scores.auditFailureReasons?.length > 0 && (
                      <ul className="text-xs space-y-1 text-slate-600">
                        {metadata.enhanced_scores.auditFailureReasons.map((reason, idx) => (
                          <li key={idx} className="flex items-start gap-1">
                            <span>•</span>
                            <span>{reason}</span>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                </>
              )}
              
              {/* Final Score Summary */}
              <Separator className="my-4" />
              <div className="flex items-center justify-between">
                <div>
                  <span className="text-sm text-slate-500">Weighted ML2 Score</span>
                  <Badge className={cn(
                    "ml-2",
                    metadata.enhanced_scores.scoreBand?.color === 'emerald' && 'bg-green-100 text-green-700',
                    metadata.enhanced_scores.scoreBand?.color === 'yellow' && 'bg-yellow-100 text-yellow-700',
                    metadata.enhanced_scores.scoreBand?.color === 'orange' && 'bg-orange-100 text-orange-700',
                    metadata.enhanced_scores.scoreBand?.color === 'red' && 'bg-red-100 text-red-700',
                    metadata.enhanced_scores.scoreBand?.color === 'rose' && 'bg-rose-100 text-rose-700'
                  )}>
                    {metadata.enhanced_scores.scoreBand?.label || 'Calculating...'}
                  </Badge>
                </div>
                <span className={cn(
                  'text-3xl font-bold',
                  getScoreColor(metadata.enhanced_scores.finalScore || 0)
                )}>
                  {metadata.enhanced_scores.finalScore || 0}%
                </span>
              </div>
            </CardContent>
          </Card>
        )}

        {/* DSR.1 DISP Submission Readiness section removed - covered by Hybrid HY.1 + HY.7 + HY.11 */}
        {/* WDR.1 What Defence Will Require removed - covered by Hybrid HY.4 + HY.11 */}

        {/* ===== CSQ SUBMISSION MAPPING ===== */}
        {metadata?.csq_mapping?.domains?.length > 0 && (
          <Card className="rounded-3xl border-0 shadow-sm" data-testid="csq-submission-mapping">
            <CardHeader className="pb-3">
              <div className="flex items-center gap-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-2xl bg-indigo-900">
                  <ClipboardList className="h-5 w-5 text-white" />
                </div>
                <div>
                  <CardTitle className="text-xl">CSQ Submission Mapping</CardTitle>
                  <CardDescription>Submission-ready guidance and copy-paste answers for the DISP Cyber Security Questionnaire</CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-8">

              {/* Submission Confidence Banner */}
              <div className={cn(
                'rounded-2xl p-5 border-2',
                metadata.csq_mapping.submission_confidence === 'LOW' && 'bg-red-50 border-red-300',
                metadata.csq_mapping.submission_confidence === 'MODERATE' && 'bg-amber-50 border-amber-300',
                metadata.csq_mapping.submission_confidence === 'HIGH' && 'bg-green-50 border-green-300',
              )} data-testid="submission-confidence">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-bold text-slate-700 uppercase tracking-wide">Submission Confidence</span>
                  <Badge className={cn(
                    'text-sm px-4 py-1 font-bold',
                    metadata.csq_mapping.submission_confidence === 'LOW' && 'bg-red-600 text-white',
                    metadata.csq_mapping.submission_confidence === 'MODERATE' && 'bg-amber-500 text-white',
                    metadata.csq_mapping.submission_confidence === 'HIGH' && 'bg-green-600 text-white',
                  )}>
                    {metadata.csq_mapping.submission_confidence}
                  </Badge>
                </div>
                <p className="text-sm text-slate-700 leading-relaxed">{metadata.csq_mapping.confidence_explanation}</p>
                <div className="flex gap-4 mt-3 text-xs text-slate-500">
                  <span>{metadata.csq_mapping.summary.no} No</span>
                  <span>{metadata.csq_mapping.summary.partial} Partial</span>
                  <span>{metadata.csq_mapping.summary.yes} Yes</span>
                  <span className="font-medium text-slate-700">{metadata.csq_mapping.summary.total} categories total</span>
                </div>
              </div>

              {metadata.csq_mapping.domains.map((domain) => (
                <div key={domain.domain_label} data-testid={`csq-domain-${domain.domain_label.replace(/\s+/g, '-').toLowerCase()}`}>
                  {/* Domain Header */}
                  <div className="flex items-center gap-2 mb-4 pb-2 border-b-2 border-indigo-100">
                    <h3 className="text-base font-bold text-indigo-900 uppercase tracking-wide">{domain.domain_label}</h3>
                    <Badge variant="outline" className="text-xs text-indigo-500 border-indigo-200">
                      {domain.blocks.length} {domain.blocks.length === 1 ? 'category' : 'categories'}
                    </Badge>
                  </div>

                  {/* CSQ Blocks */}
                  <div className="space-y-5">
                    {domain.blocks.map((block, bi) => (
                      <div
                        key={bi}
                        className={cn(
                          'rounded-xl border p-4',
                          block.compliance_status === 'non_compliant' ? 'border-red-200 bg-white' :
                          block.compliance_status === 'partially_compliant' ? 'border-amber-200 bg-white' :
                          'border-green-200 bg-white'
                        )}
                        data-testid={`csq-block-${bi}`}
                      >
                        {/* Category + Response Direction */}
                        <div className="flex items-start justify-between gap-3 mb-3">
                          <div>
                            <div className="text-[11px] font-semibold text-indigo-600 uppercase tracking-wide mb-0.5">
                              {block.csq_category}
                            </div>
                            <h4 className="text-sm font-bold text-slate-900">{block.combined_name}</h4>
                          </div>
                          <Badge className={cn(
                            'text-xs shrink-0 px-2.5',
                            block.response_direction === 'No' ? 'bg-red-100 text-red-700 border-red-200' :
                            block.response_direction === 'Partial' ? 'bg-amber-100 text-amber-700 border-amber-200' :
                            'bg-green-100 text-green-700 border-green-200'
                          )}>
                            CSQ Response: {block.response_direction}
                          </Badge>
                        </div>

                        {/* Submission Guidance */}
                        <div className="mb-3">
                          <p className="text-[11px] font-semibold text-slate-500 uppercase tracking-wide mb-1">Submission Guidance</p>
                          <p className="text-xs text-slate-700 leading-relaxed">{block.submission_guidance}</p>
                        </div>

                        {/* Defence Review Risk */}
                        <div className="mb-3">
                          <p className="text-[11px] font-semibold text-slate-500 uppercase tracking-wide mb-1">Defence Review Risk</p>
                          <p className={cn(
                            'text-xs font-medium',
                            block.compliance_status === 'non_compliant' ? 'text-red-700' :
                            block.compliance_status === 'partially_compliant' ? 'text-amber-700' :
                            'text-green-700'
                          )}>
                            {block.review_risk}
                          </p>
                        </div>

                        {/* CSQ Response (Ready to Submit) */}
                        <div className="mb-3 rounded-lg border border-indigo-100 bg-indigo-50/30 p-3">
                          <p className="text-[11px] font-bold text-indigo-700 uppercase tracking-wide mb-1">CSQ Response (Ready to Submit)</p>
                          <p className="text-xs text-slate-800 leading-relaxed italic">{block.csq_response_text}</p>
                        </div>

                        {/* Supporting Evidence for Submission */}
                        <div className="mb-3">
                          <p className="text-[11px] font-semibold text-slate-500 uppercase tracking-wide mb-1">Supporting Evidence for Submission</p>
                          <div className="space-y-1.5">
                            {(block.supporting_evidence || []).map((ev, ei) => (
                              <div key={ei} className="flex items-start gap-1.5 text-[11px]">
                                <span className="w-1 h-1 rounded-full bg-indigo-400 shrink-0 mt-1.5" />
                                <div>
                                  <span className="text-slate-800 font-medium">{typeof ev === 'string' ? ev : ev.item}</span>
                                  {ev.context && <span className="text-slate-400 ml-1">({ev.context})</span>}
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>

                        {/* Minimum Viable Submission Path */}
                        {block.minimum_viable_path?.length > 0 && (
                          <div className="mb-3 rounded-lg border border-indigo-100 bg-slate-50 p-3">
                            <p className="text-[11px] font-bold text-indigo-700 uppercase tracking-wide mb-2">Minimum Viable Submission Path</p>
                            <div className="space-y-1.5">
                              {block.minimum_viable_path.map((step, si) => (
                                <div key={si} className="flex items-start gap-2 text-[11px] text-slate-700">
                                  <span className="text-indigo-500 font-bold shrink-0">{si + 1}.</span>
                                  {step}
                                </div>
                              ))}
                            </div>
                          </div>
                        )}

                        {/* Submission Decision */}
                        <div className={cn(
                          'rounded-lg px-3 py-2 border',
                          block.compliance_status === 'non_compliant' ? 'bg-red-50 border-red-200' :
                          block.compliance_status === 'partially_compliant' ? 'bg-amber-50 border-amber-200' :
                          'bg-green-50 border-green-200'
                        )}>
                          <p className={cn(
                            'text-xs font-semibold',
                            block.compliance_status === 'non_compliant' ? 'text-red-800' :
                            block.compliance_status === 'partially_compliant' ? 'text-amber-800' :
                            'text-green-800'
                          )}>
                            {block.submission_decision}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}

              {/* CSQ.2 Overall CSQ Submission Insight removed - covered by Hybrid HY.8 + HY.11 */}
              {/* CSQ.3 Minimum Path to Submission Readiness removed - covered by Hybrid HY.8 numbered path */}

            </CardContent>
          </Card>
        )}

        {/* GC.1 Gating Controls Summary card removed - covered by Hybrid HY.6 Gating Control Assessment */}

        {/* Top 3 Weakest Domains */}
        <Card className="rounded-3xl border-0 shadow-sm">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <TrendingDown className="h-5 w-5 text-red-500" />
              Top 3 Weakest Domains
            </CardTitle>
            <CardDescription>
              Domains requiring the most attention
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {weakestDomains.map((domain, idx) => (
                <div key={domain.domain_id} className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className={cn(
                      'w-8 h-8 rounded-xl flex items-center justify-center text-sm font-bold',
                      getScoreBg(domain.score),
                      getScoreColor(domain.score)
                    )}>
                      {idx + 1}
                    </div>
                    <div>
                      <p className="font-medium text-slate-900">{domain.domain_name}</p>
                      <p className="text-sm text-slate-500">
                        {domain.answered}/{domain.total} questions • {domain.weight}% weight
                      </p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className={cn('text-2xl font-bold', getScoreColor(domain.score))}>
                      {domain.score}%
                    </p>
                    <Badge className={cn(getScoreBg(domain.score), getScoreColor(domain.score))}>
                      {getMaturityLabel(domain.score)}
                    </Badge>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Priority Gaps */}
        <Card className="rounded-3xl border-0 shadow-sm">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Target className="h-5 w-5 text-amber-500" />
              Critical & High Priority Gaps ({priorityGaps.filter(g => g.priority === 'critical' || g.priority === 'high').length})
            </CardTitle>
            <CardDescription>
              Issues requiring immediate attention
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-3 max-h-80 overflow-y-auto">
              {priorityGaps
                .filter(g => g.priority === 'critical' || g.priority === 'high')
                .map((gap, idx) => (
                  <div 
                    key={`${gap.domain}-${idx}`}
                    className="flex items-start justify-between p-4 rounded-xl border bg-white"
                  >
                    <div className="flex-1 pr-4">
                      <p className="font-medium text-slate-900">{gap.gap}</p>
                      <p className="text-sm text-slate-500 mt-1">
                        {SECTION_CONFIG[gap.domain]?.title}
                      </p>
                      {gap.recommended_components?.length > 0 && (
                        <div className="flex flex-wrap gap-1 mt-2">
                          {gap.recommended_components.slice(0, 3).map(comp => (
                            <Badge key={comp} variant="outline" className="text-xs">
                              {COMPONENT_LABELS[comp] || comp}
                            </Badge>
                          ))}
                        </div>
                      )}
                    </div>
                    <Badge className={getPriorityColor(gap.priority)}>
                      {gap.priority}
                    </Badge>
                  </div>
                ))}
            </div>
          </CardContent>
        </Card>

        {/* Technology Stack Status */}
        <Card className="rounded-3xl border-0 shadow-sm" data-testid="stack-status-card">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Layers className="h-5 w-5 text-blue-500" />
              Technology Stack Status
            </CardTitle>
            <CardDescription>
              Component presence, maturity, and recommended actions
            </CardDescription>
          </CardHeader>
          <CardContent>
            {/* Summary Stats */}
            {metadata?.stack_summary && (
              <div className="grid grid-cols-5 gap-2 mb-6">
                <div className="rounded-xl bg-green-50 border border-green-200 p-3 text-center">
                  <p className="text-2xl font-bold text-green-700">{metadata.stack_summary.implemented_count}</p>
                  <p className="text-[10px] text-green-600 font-medium">Implemented</p>
                </div>
                <div className="rounded-xl bg-orange-50 border border-orange-200 p-3 text-center">
                  <p className="text-2xl font-bold text-orange-700">{metadata.stack_summary.needs_improvement_count}</p>
                  <p className="text-[10px] text-orange-600 font-medium">Needs Improvement</p>
                </div>
                <div className="rounded-xl bg-yellow-50 border border-yellow-200 p-3 text-center">
                  <p className="text-2xl font-bold text-amber-700">{metadata.stack_summary.partial_count}</p>
                  <p className="text-[10px] text-amber-600 font-medium">Partial</p>
                </div>
                <div className="rounded-xl bg-red-50 border border-red-200 p-3 text-center">
                  <p className="text-2xl font-bold text-red-700">{metadata.stack_summary.missing_count}</p>
                  <p className="text-[10px] text-red-600 font-medium">Missing</p>
                </div>
                <div className="rounded-xl bg-slate-50 border border-slate-200 p-3 text-center">
                  <p className="text-2xl font-bold text-slate-500">{metadata.stack_summary.planned_count}</p>
                  <p className="text-[10px] text-slate-400 font-medium">Planned</p>
                </div>
              </div>
            )}
            
            {metadata?.stack_analysis?.stack_alignment_score !== undefined && (
              <div className="flex items-center justify-between mb-4 p-3 rounded-xl bg-slate-50 border">
                <span className="text-sm text-slate-600">Stack Alignment Score</span>
                <div className="flex items-center gap-2">
                  <Progress value={metadata.stack_analysis.stack_alignment_score} className="h-2 w-24" />
                  <span className={cn(
                    "text-sm font-semibold",
                    metadata.stack_analysis.stack_alignment_score >= 70 ? 'text-green-600' :
                    metadata.stack_analysis.stack_alignment_score >= 40 ? 'text-amber-600' : 'text-red-600'
                  )}>
                    {metadata.stack_analysis.stack_alignment_score}%
                  </span>
                </div>
              </div>
            )}

            {/* Stack rows grouped by layer */}
            {metadata?.stack_status_rows && (() => {
              const layerOrder = ['Enforcement Layer', 'Validation Layer', 'Monitoring Layer', 'Compliance Layer'];
              const layerIcons = {
                'Enforcement Layer': Shield,
                'Validation Layer': Activity,
                'Monitoring Layer': Server,
                'Compliance Layer': ClipboardList
              };
              const statusBadgeStyles = {
                'Implemented': 'bg-green-100 text-green-700',
                'Needs Improvement': 'bg-orange-100 text-orange-700',
                'Partially Implemented': 'bg-yellow-100 text-amber-700',
                'Missing': 'bg-red-100 text-red-700',
                'Planned': 'bg-slate-100 text-slate-500'
              };
              return (
                <div className="space-y-6">
                  {layerOrder.map(layerLabel => {
                    const rows = metadata.stack_status_rows.filter(r => r.layer === layerLabel);
                    if (rows.length === 0) return null;
                    const LayerIcon = layerIcons[layerLabel] || Layers;
                    return (
                      <div key={layerLabel}>
                        <h4 className="font-semibold text-slate-900 mb-3 flex items-center gap-2">
                          <LayerIcon className="h-4 w-4" />
                          {layerLabel}
                        </h4>
                        <div className="space-y-2">
                          {rows.map(row => (
                            <div 
                              key={row.component_label}
                              className="flex items-start gap-3 p-3 rounded-xl border bg-white"
                            >
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2">
                                  <span className="text-sm font-medium text-slate-900">{row.component_label}</span>
                                </div>
                                <p className="text-[11px] text-slate-400 mt-0.5">{row.requirement_text}</p>
                                <p className="text-xs text-slate-600 mt-1">{row.action}</p>
                              </div>
                              <Badge className={cn(
                                "text-xs whitespace-nowrap shrink-0",
                                statusBadgeStyles[row.status] || 'bg-slate-100 text-slate-500'
                              )}>
                                {row.status}
                              </Badge>
                            </div>
                          ))}
                        </div>
                      </div>
                    );
                  })}
                </div>
              );
            })()}
          </CardContent>
        </Card>

        {/* RR.1 Phased Remediation Roadmap removed - covered by Hybrid HY.9 Path to DISP Readiness */}

        {/* DST.1 Complete Domain Breakdown removed - covered by Hybrid HY.4 Decision View + HY.5 Per-Domain Risk & Priority */}

        {/* ===== NEW: Recommended Next Step (Accelerator Positioning) ===== */}
        <Card className="rounded-3xl border-0 shadow-sm bg-gradient-to-br from-slate-900 to-slate-800 text-white" data-testid="accelerator-section">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-white">
              <Rocket className="h-5 w-5" />
              Recommended Next Step
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-slate-200 leading-relaxed text-sm">
              To achieve DISP submission readiness, the organisation must undertake a structured
              uplift and validation program beyond initial discovery.
            </p>
            <p className="text-slate-300 text-sm leading-relaxed">
              This program is designed to:
            </p>
            <ul className="space-y-2">
              {[
                'Validate control implementation in detail',
                'Collect and structure audit-ready evidence',
                'Prepare CSQ-aligned responses',
                'Support submission through the DISP portal',
              ].map((item, idx) => (
                <li key={idx} className="flex items-center gap-2.5 text-sm text-slate-200">
                  <CheckCircle2 className="h-4 w-4 text-green-400 shrink-0" />
                  {item}
                </li>
              ))}
            </ul>
            {dispReadiness.submission_status === 'NOT_READY' && (
              <div className="rounded-xl bg-red-500/15 border border-red-400/30 p-4 mt-3">
                <p className="text-sm text-red-200 leading-relaxed">
                  Based on current findings, the organisation is not yet in a position to 
                  successfully complete the CSQ without further structured uplift.
                </p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* ===== ML2 IMPLEMENTATION BLUEPRINT ===== */}
        {blueprint && (
          <>
            {/* Section Header */}
            <Card className="rounded-3xl border-0 shadow-sm border-l-4 border-l-indigo-500" data-testid="blueprint-section">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-xl">
                  <Wrench className="h-5 w-5 text-indigo-500" />
                  ML2 Implementation Blueprint
                </CardTitle>
                <CardDescription>
                  Technical Enablement Plan — translating assessment findings into a structured deployment and implementation plan
                </CardDescription>
                {blueprint.severeGapWarning && (
                  <div className="mt-3 rounded-xl bg-red-50 border-2 border-red-300 p-4">
                    <p className="text-sm font-semibold text-red-800">{blueprint.severeGapWarning}</p>
                  </div>
                )}
              </CardHeader>
            </Card>

            {/* 1. Capability Model */}
            <Card className="rounded-3xl border-0 shadow-sm" data-testid="blueprint-capability">
              <CardHeader>
                <CardTitle className="text-base flex items-center gap-2">
                  <span className="flex h-6 w-6 items-center justify-center rounded-full bg-indigo-100 text-xs font-bold text-indigo-700">1</span>
                  Capability Model
                </CardTitle>
                <CardDescription>ML2 compliance requires four core capabilities</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <p className="text-sm text-slate-700 leading-relaxed">{blueprint.capabilityModel.narrative}</p>

                <div className="grid gap-3 md:grid-cols-2">
                  {blueprint.capabilityModel.capabilities.map((cap) => (
                    <div key={cap.name} className="rounded-xl border border-slate-200 p-3">
                      <p className="text-sm font-semibold text-slate-900">{cap.name}</p>
                      <p className="text-xs text-slate-500 mt-1">{cap.description}</p>
                    </div>
                  ))}
                </div>

                {blueprint.capabilityModel.emphasis.length > 0 && (
                  <div className="space-y-2 mt-2">
                    {blueprint.capabilityModel.emphasis.map((e, idx) => (
                      <div key={idx} className="flex items-start gap-2 rounded-xl bg-amber-50 border border-amber-200 p-3">
                        <AlertTriangle className="h-4 w-4 text-amber-600 mt-0.5 shrink-0" />
                        <p className="text-xs text-amber-800">{e}</p>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* 2. Reference Architecture */}
            <Card className="rounded-3xl border-0 shadow-sm" data-testid="blueprint-architecture">
              <CardHeader>
                <CardTitle className="text-base flex items-center gap-2">
                  <span className="flex h-6 w-6 items-center justify-center rounded-full bg-indigo-100 text-xs font-bold text-indigo-700">2</span>
                  Reference Architecture
                </CardTitle>
                <CardDescription>Logical architecture mapped from the recommended technology stack</CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Blueprint Current State Groups */}
                {metadata?.blueprint_current_state && (
                  <div className="grid gap-3 md:grid-cols-2">
                    {[
                      { title: 'Already in Place', items: metadata.blueprint_current_state.already_in_place, color: 'green', Icon: CheckCircle2 },
                      { title: 'Existing but Needs Uplift', items: metadata.blueprint_current_state.uplift_required, color: 'amber', Icon: AlertTriangle },
                      { title: 'Net-New Components Required', items: metadata.blueprint_current_state.net_new_required, color: 'red', Icon: XCircle },
                      { title: 'Recommended Enhancements', items: metadata.blueprint_current_state.optional_enhancements, color: 'blue', Icon: TrendingUp }
                    ].map(group => {
                      if (!group.items || group.items.length === 0) return null;
                      return (
                        <div key={group.title} className={cn(
                          "rounded-xl border p-3",
                          `bg-${group.color}-50 border-${group.color}-200`
                        )} style={{
                          backgroundColor: group.color === 'green' ? '#f0fdf4' : group.color === 'amber' ? '#fffbeb' : group.color === 'red' ? '#fef2f2' : '#eff6ff',
                          borderColor: group.color === 'green' ? '#bbf7d0' : group.color === 'amber' ? '#fde68a' : group.color === 'red' ? '#fecaca' : '#bfdbfe'
                        }}>
                          <div className="flex items-center gap-1.5 mb-2">
                            <group.Icon className={cn(
                              "h-3.5 w-3.5",
                              group.color === 'green' ? 'text-green-600' : group.color === 'amber' ? 'text-amber-600' : group.color === 'red' ? 'text-red-600' : 'text-blue-600'
                            )} />
                            <h5 className="text-xs font-semibold text-slate-800">{group.title}</h5>
                          </div>
                          <div className="space-y-1">
                            {group.items.map(comp => (
                              <p key={comp.id} className="text-xs text-slate-700">{comp.label}</p>
                            ))}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}

                {/* Layer Architecture */}
                <div className="grid gap-4 md:grid-cols-2">
                  {Object.entries(blueprint.referenceArchitecture.layers).map(([layerId, layer]) => (
                    <div key={layerId} className="rounded-2xl border bg-slate-50 p-4">
                      <div className="flex items-center gap-2 mb-1">
                        {layerId === 'enforcement_layer' && <Shield className="h-4 w-4 text-blue-500" />}
                        {layerId === 'validation_layer' && <Activity className="h-4 w-4 text-teal-500" />}
                        {layerId === 'monitoring_layer' && <Server className="h-4 w-4 text-violet-500" />}
                        {layerId === 'compliance_layer' && <ClipboardList className="h-4 w-4 text-emerald-500" />}
                        <h4 className="text-sm font-semibold text-slate-900">{layer.name}</h4>
                      </div>
                      <p className="text-xs text-slate-500 mb-3">{layer.description}</p>
                      {layer.tools.length > 0 ? (
                        <div className="space-y-1.5">
                          {layer.tools.map((tool) => (
                            <div key={tool.id} className="flex items-start gap-2 text-xs">
                              <CheckCircle2 className="h-3.5 w-3.5 text-green-500 mt-0.5 shrink-0" />
                              <div>
                                <span className="font-medium text-slate-800">{tool.label}</span>
                                <span className="text-slate-500"> — {tool.role}</span>
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <p className="text-xs text-slate-400 italic">No components recommended</p>
                      )}
                      {layer.gaps.map((gap, idx) => (
                        <div key={idx} className="mt-2 rounded-lg bg-red-50 border border-red-200 p-2">
                          <p className="text-xs text-red-700 flex items-start gap-1">
                            <XCircle className="h-3.5 w-3.5 mt-0.5 shrink-0" />
                            {gap}
                          </p>
                        </div>
                      ))}
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* 3. Continuous Compliance Platform */}
            <Card className="rounded-3xl border-0 shadow-sm" data-testid="blueprint-compliance-platform">
              <CardHeader>
                <CardTitle className="text-base flex items-center gap-2">
                  <span className="flex h-6 w-6 items-center justify-center rounded-full bg-indigo-100 text-xs font-bold text-indigo-700">3</span>
                  Continuous Compliance Platform
                </CardTitle>
                <CardDescription>Deployment requirements and integration specification</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {blueprint.continuousCompliance.isCritical && blueprint.continuousCompliance.criticalNote && (
                  <div className="rounded-xl bg-red-50 border-2 border-red-300 p-4">
                    <p className="text-sm font-semibold text-red-800">{blueprint.continuousCompliance.criticalNote}</p>
                  </div>
                )}

                {/* VM Sizing */}
                <div className="rounded-xl bg-slate-50 border border-slate-200 p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <HardDrive className="h-4 w-4 text-slate-500" />
                    <span className="text-sm font-semibold text-slate-900">Deployment Sizing: {blueprint.continuousCompliance.vmSizing.label}</span>
                  </div>
                  <p className="text-xs text-slate-600 font-mono mb-1">{blueprint.continuousCompliance.vmSizing.spec}</p>
                  <p className="text-xs text-slate-500">{blueprint.continuousCompliance.vmSizing.reason}</p>
                </div>

                {/* Integration Points */}
                <div>
                  <h4 className="text-sm font-semibold text-slate-700 mb-2">Integration Points</h4>
                  <div className="space-y-2">
                    {blueprint.continuousCompliance.integrationPoints.map((ip, idx) => (
                      <div key={idx} className="flex items-start gap-3 p-3 rounded-xl border bg-white">
                        <Globe className="h-4 w-4 text-indigo-400 mt-0.5 shrink-0" />
                        <div>
                          <p className="text-sm font-medium text-slate-900">{ip.system}</p>
                          <p className="text-xs text-slate-500">{ip.purpose}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Functional Role */}
                <div>
                  <h4 className="text-sm font-semibold text-slate-700 mb-2">Functional Role</h4>
                  <div className="space-y-1.5">
                    {blueprint.continuousCompliance.functionalRole.map((role, idx) => (
                      <div key={idx} className="flex items-start gap-2 text-sm text-slate-700">
                        <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-indigo-400 shrink-0" />
                        {role}
                      </div>
                    ))}
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* 4. Evidence Generation & Management Model */}
            <Card className="rounded-3xl border-0 shadow-sm" data-testid="blueprint-evidence">
              <CardHeader>
                <CardTitle className="text-base flex items-center gap-2">
                  <span className="flex h-6 w-6 items-center justify-center rounded-full bg-indigo-100 text-xs font-bold text-indigo-700">4</span>
                  Evidence Generation & Management
                </CardTitle>
                <CardDescription>How evidence will be produced and managed for each control area</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {blueprint.evidenceModel.isCritical && blueprint.evidenceModel.criticalNote && (
                  <div className="rounded-xl bg-red-50 border-2 border-red-300 p-4">
                    <p className="text-sm font-semibold text-red-800">{blueprint.evidenceModel.criticalNote}</p>
                  </div>
                )}

                {blueprint.evidenceModel.mappings.length > 0 && (
                  <div className="rounded-xl border overflow-hidden">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="bg-slate-50 border-b">
                          <th className="text-left p-3 text-xs font-semibold text-slate-600">Control Area</th>
                          <th className="text-left p-3 text-xs font-semibold text-slate-600">Evidence Source</th>
                          <th className="text-left p-3 text-xs font-semibold text-slate-600">Output</th>
                        </tr>
                      </thead>
                      <tbody>
                        {blueprint.evidenceModel.mappings.map((m, idx) => (
                          <tr key={idx} className="border-b last:border-b-0">
                            <td className="p-3 font-medium text-slate-900 text-xs">{m.control}</td>
                            <td className="p-3 text-xs text-slate-600">{m.source}</td>
                            <td className="p-3 text-xs text-slate-600">{m.output}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}

                <div>
                  <h4 className="text-sm font-semibold text-slate-700 mb-2">Defined Outputs</h4>
                  <div className="space-y-1.5">
                    {blueprint.evidenceModel.outputs.map((output, idx) => (
                      <div key={idx} className="flex items-start gap-2 text-sm text-slate-700">
                        <FolderCheck className="h-4 w-4 text-green-500 mt-0.5 shrink-0" />
                        {output}
                      </div>
                    ))}
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* 5. Implementation Phases */}
            <Card className="rounded-3xl border-0 shadow-sm" data-testid="blueprint-phases">
              <CardHeader>
                <CardTitle className="text-base flex items-center gap-2">
                  <span className="flex h-6 w-6 items-center justify-center rounded-full bg-indigo-100 text-xs font-bold text-indigo-700">5</span>
                  Implementation Phases
                </CardTitle>
                <CardDescription>Phased deployment plan from platform foundation through DISP readiness</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-6">
                  {Object.entries(blueprint.phases).map(([phaseId, phase]) => {
                    const phaseColors = {
                      phase0: { dot: 'bg-indigo-500', bg: 'bg-indigo-50', border: 'border-indigo-200', text: 'text-indigo-800' },
                      phase1: { dot: 'bg-red-500', bg: 'bg-red-50', border: 'border-red-200', text: 'text-red-800' },
                      phase2: { dot: 'bg-amber-500', bg: 'bg-amber-50', border: 'border-amber-200', text: 'text-amber-800' },
                      phase3: { dot: 'bg-green-500', bg: 'bg-green-50', border: 'border-green-200', text: 'text-green-800' }
                    };
                    const colors = phaseColors[phaseId] || phaseColors.phase0;

                    return (
                      <div key={phaseId} className="relative pl-8 pb-2 border-l-2 border-slate-200 last:border-l-0">
                        <div className={cn('absolute left-0 top-0 -translate-x-1/2 w-4 h-4 rounded-full', colors.dot)} />
                        <h4 className="font-semibold text-slate-900">{phase.name}</h4>
                        <p className="text-sm text-slate-500 mb-3">{phase.description}</p>

                        {phase.isEmpty ? (
                          <p className="text-xs text-slate-400 italic">{phase.emptyNote}</p>
                        ) : (
                          <div className="space-y-1.5">
                            {phase.items.map((item, idx) => (
                              <div key={idx} className="flex items-start gap-2 text-sm">
                                <div className={cn('w-1.5 h-1.5 rounded-full mt-2 shrink-0', colors.dot)} />
                                <span className="text-slate-700">{item}</span>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          </>
        )}

        {/* Export Footer */}
        <div className="flex flex-wrap justify-center gap-4 pt-6 pb-12">
          <Button 
            variant="outline" 
            onClick={onBack}
            className="rounded-xl"
          >
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back to Assessment
          </Button>
          <Button 
            variant="outline" 
            onClick={handleExportJSON}
            className="rounded-xl"
          >
            <FileText className="mr-2 h-4 w-4" />
            Export JSON
          </Button>
          <Button 
            variant="outline"
            onClick={handleExportExecutiveSnapshot}
            className="rounded-xl border-indigo-200 text-indigo-700 hover:bg-indigo-50"
            data-testid="export-executive-snapshot-footer-btn"
          >
            <Rocket className="mr-2 h-4 w-4" />
            Generate Executive Snapshot PDF
          </Button>
          <Button 
            onClick={handleExportPDF}
            className="rounded-xl bg-slate-900 hover:bg-slate-800"
          >
            <Download className="mr-2 h-4 w-4" />
            Export PDF Report
          </Button>
        </div>
      </div>
    </div>
  );
}
